import React from 'react'

import { Controller, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { RegisterRequestDTO } from '@/core/api/dtos/request/auth/register'
import { DemographicAgeGroup } from '@/core/api/entities/demographicAgeGroup'
import { DemographicSocioeconomicGroup } from '@/core/api/entities/demographicSocioeconomicGroup'
import { Button } from '@/shared/components/atoms/Button'
import { ErrorMessage } from '@/shared/components/atoms/ErrorMessage'
import Icon from '@/shared/components/atoms/Icon'
import { Label } from '@/shared/components/atoms/Label/Label'

interface RegisterStep6Props {
	form: UseFormReturn<RegisterRequestDTO>
	isLoading: boolean
	onNextStep: (data: Partial<RegisterRequestDTO>) => void
	onPrevStep: () => void
	demographicAgeGroups: DemographicAgeGroup[]
	demographicSocioeconomicGroups: DemographicSocioeconomicGroup[]
}

export const RegisterStep6: React.FC<RegisterStep6Props> = ({
	form,
	isLoading,
	onNextStep,
	demographicAgeGroups,
}) => {
	const { t } = useTranslation('auth')

	const ageRanges = demographicAgeGroups.map((group) => ({
		key: group.key,
		label: t(`demographicAgeGroups.${group.key}`),
	}))

	const socioeconomicLevels = [
		{
			key: 'luxury_clientele',
			value: 'luxury_clientele',
			label: t('registerStep5.socioeconomicLevels.luxury_clientele'),
		},
		{
			key: 'premium_seekers',
			value: 'premium_seekers',
			label: t('registerStep5.socioeconomicLevels.premium_seekers'),
		},
		{
			key: 'middle_range_buyers',
			value: 'middle_range_buyers',
			label: t('registerStep5.socioeconomicLevels.middle_range_buyers'),
		},
		{
			key: 'budget_conscious',
			value: 'budget_conscious',
			label: t('registerStep5.socioeconomicLevels.budget_conscious'),
		},
		{
			key: 'indifferent',
			value: 'indifferent',
			label: t('registerStep5.socioeconomicLevels.indifferent'),
		},
	]

	const selectedAges = form.watch('demographic_ages') || []
	const selectedSocioeconomicLevels =
		form.watch('demographic_socioeconomics') || []

	const isFormValid =
		selectedAges.length > 0 &&
		selectedSocioeconomicLevels.length > 0 &&
		Object.keys(form.formState.errors).length === 0

	const handleAgeToggle = (ageKey: string) => {
		const currentAges = form.getValues('demographic_ages') || []
		const isSelected = currentAges.includes(ageKey)

		if (isSelected) {
			const newAges = currentAges.filter((age) => age !== ageKey)
			form.setValue('demographic_ages', newAges)
		} else {
			if (ageKey === 'indifferent') {
				form.setValue('demographic_ages', ['indifferent'])
			} else {
				const filteredAges = currentAges.filter((age) => age !== 'indifferent')
				form.setValue('demographic_ages', [...filteredAges, ageKey])
			}
		}
	}

	const handleSocioeconomicToggle = (mappedValue: string) => {
		const currentLevels: string[] =
			form.getValues('demographic_socioeconomics') || []
		const isSelected = currentLevels.includes(mappedValue)

		if (isSelected) {
			const newLevels = currentLevels.filter((level) => level !== mappedValue)
			form.setValue('demographic_socioeconomics', newLevels)
		} else {
			if (mappedValue === 'indifferent') {
				form.setValue('demographic_socioeconomics', ['indifferent'])
			} else {
				const filtered = currentLevels.filter(
					(level) => level !== 'indifferent'
				)
				form.setValue('demographic_socioeconomics', [...filtered, mappedValue])
			}
		}
	}

	const handleNextStep = async () => {
		const isValid = await form.trigger([
			'demographic_ages',
			'demographic_socioeconomics',
		])

		if (isValid) {
			const values = form.getValues()
			onNextStep({
				demographic_ages: values.demographic_ages,
				demographic_socioeconomics: values.demographic_socioeconomics,
			})
		}
	}

	return (
		<div className="space-y-6 flex flex-col justify-between h-full">
			<div>
				<h1 className="register-title font-bold mb-4">
					{t('registerStep5.title')}
				</h1>
				<p className="mb-8">{t('registerStep5.subtitle')}</p>

				<div className="space-y-4">
					<div>
						<Label className="mb-2 block">{t('registerStep5.age')}</Label>
						<Controller
							control={form.control}
							name="demographic_ages"
							render={({ fieldState: { error } }) => (
								<div>
									<div className="flex mt-2 flex-wrap justify-left gap-1.5 max-w-7xl mx-auto">
										{ageRanges.map((age) => (
											<button
												key={age.key}
												type="button"
												onClick={() => handleAgeToggle(age.key)}
												className={`flex flex-row justify-center gap-1 items-center px-2.5 cursor-pointer py-1 rounded-full text-sm font-medium transition-all ${
													selectedAges.includes(age.key)
														? 'dark:bg-white dark:text-black bg-black text-white'
														: 'bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-[#292929] dark:text-white dark:hover:bg-[#292929]/80'
												}`}
											>
												{selectedAges.includes(age.key) && (
													<Icon
														name={'checkmark-black'}
														width="16px"
														height="16px"
													/>
												)}
												{age.label}
											</button>
										))}
									</div>
									{error && <ErrorMessage error={error.message ?? ''} />}
								</div>
							)}
						/>

						<p className="text-muted-foreground mt-2">
							{selectedAges?.length} {t('registerStep5.selected')}
						</p>
					</div>

					<div>
						<Label className="mb-2 block">
							{t('registerStep5.socioeconomicLevel')}
						</Label>
						<Controller
							control={form.control}
							name="demographic_socioeconomics"
							render={({ fieldState: { error } }) => (
								<div>
									<div className="flex justify-left gap-1.5 max-w-7xl mx-auto flex-wrap">
										{socioeconomicLevels.map((opt) => {
											const active = selectedSocioeconomicLevels.includes(
												opt.value
											)
											return (
												<button
													key={opt.key}
													type="button"
													onClick={() => handleSocioeconomicToggle(opt.value)}
													className={`flex flex-row justify-center gap-1 items-center px-2.5 cursor-pointer py-1 rounded-full text-sm font-medium transition-all ${
														active
															? 'dark:bg-white dark:text-black bg-black text-white'
															: 'bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-[#292929] dark:text-white dark:hover:bg-[#292929]/80'
													}`}
												>
													{active && (
														<Icon
															name={'checkmark-black'}
															width="16px"
															height="16px"
														/>
													)}
													{opt.label}
												</button>
											)
										})}
									</div>
									{error && <ErrorMessage error={error.message ?? ''} />}
								</div>
							)}
						/>
						<p className="text-muted-foreground mt-2">
							{' '}
							{selectedSocioeconomicLevels?.length}{' '}
							{t('registerStep5.selected')}
						</p>
					</div>
				</div>
			</div>

			<div className="mt-8 flex gap-4">
				<Button
					onClick={handleNextStep}
					className="px-8 py-3 cursor-pointer flex-1 h-10 text-sm font-medium rounded-full"
					disabled={isLoading || !isFormValid}
				>
					{isLoading ? 'Loading...' : t('register.following')}
				</Button>
			</div>
		</div>
	)
}
